// @ts-nocheck
// Call-script card — cti-v3-style panel showing the script that
// the backend resolved for the current call. Sourced from the
// existing `contextScript` state populated by
//   GET /api/scripts/by-context?campaign_id=&call_group_id=
// (see CTIAgentUI.tsx line ~830). Backend schema at time of writing:
//   { id, title, short_description, description /* rich HTML */ }
// plus a `source` sibling telling us which lookup level won
// (campaign / group / default) so we can label it.
//
// Layout mirrors the reference cti-v3 screenshot:
//   Header:   icon + title + version chip + right-side tab pill
//   Progress: segmented bar (only meaningful when the script has
//             a real `steps[]` — schema doesn't ship steps yet, so
//             we render a single-segment placeholder that stays
//             filled)
//   Body:     step badge (Say / Ask / etc.) + step title + rich
//             text body. When the schema is flat (title +
//             description only) we render title as the step title
//             and description as the body.
//   Hint:     yellow-tinted assist line, driven by
//             `script.hint` when present.
//   Foot:     Back / Next-step buttons + assist chip on the right.
//             Buttons are disabled when the script has fewer than
//             two navigable steps.
import React, { useMemo, useState } from 'react';
import {
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiZap,
} from 'react-icons/fi';
import { useCallStore } from '@/router/zustand/callStore';

// Force any links/images inside a script's rich-HTML body to open
// in a new tab — an agent clicking a link embedded in a script used
// to navigate the whole SPA away from the live-call screen mid-call.
export function forceScriptLinksNewTab(html) {
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return html;
  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

// Read a rich HTML description safely. The backend already sanitizes
// (see rich-text editor in EditCallScriptPage), so we mount it as
// dangerouslySetInnerHTML — matches how the campaign detail page
// renders the same field.
function ScriptBody({ html }) {
  if (!html) return null;
  return (
    <div
      className="csc-body"
      dangerouslySetInnerHTML={{ __html: forceScriptLinksNewTab(String(html)) }}
    />
  );
}

function tabButton(active, key, label, setTab) {
  return (
    <button
      type="button"
      className={`csc-tab${active ? ' csc-tab--active' : ''}`}
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  );
}

export default function CallScriptCard({ contextScript, contextScriptSource, contextScriptLoading, alwaysShow = false, showObjectionsTab = true }) {
  const callState = useCallStore((s) => s.callState);
  const dispositionActive = useCallStore((s) => s.dispositionActive);
  const dispositionOpen = useCallStore((s) => s.dispositionOpen);
  // "not idle" used to count 'dialing' (outbound pre-answer) as active too,
  // showing the script before the customer was ever even ringing. Only
  // ringing ('incoming') and truly-connected states count now — wrap-up
  // (disposition still open/active) is kept separately so the script stays
  // visible for reference while the agent finishes disposing, even once
  // callState itself has already dropped back to idle.
  const isActive = ['incoming', 'oncall', 'connected', 'hold', 'conference'].includes(callState)
    || Boolean(dispositionActive || dispositionOpen);

  const [tab, setTab] = useState('script');
  const [stepIdx, setStepIdx] = useState(0);

  // Steps model — backend currently returns a flat description, so
  // treat the whole script as one step. Some tenants may have future
  // extended shape ({ steps: [{ title, body, kind, hint }] }) so
  // support that path too without breaking the flat case.
  const steps = useMemo(() => {
    if (!contextScript) return [];
    if (Array.isArray(contextScript?.steps) && contextScript.steps.length > 0) {
      return contextScript.steps;
    }
    return [{
      kind: 'Say',
      title: contextScript?.short_description || contextScript?.title || 'Script',
      body: contextScript?.description || '',
      hint: contextScript?.hint || null,
    }];
  }, [contextScript]);

  const objections = useMemo(() => {
    if (!contextScript) return [];
    return Array.isArray(contextScript?.objections) ? contextScript.objections : [];
  }, [contextScript]);

  // Clamp step index if the script switches mid-call to a shorter one.
  const idx = Math.min(stepIdx, Math.max(0, steps.length - 1));
  const currentStep = steps[idx] || null;

  // Hide the card entirely when there's no call AND no script data.
  // (During an idle preview state the customer-profile empty card
  // above already conveys "no active caller" — a second placeholder
  // would double the noise.) `alwaysShow` opts a caller out of this —
  // default false so every existing caller (production's IdleScreen.tsx
  // included) keeps this exact behavior unchanged.
  if (!alwaysShow && !contextScript && !isActive && !contextScriptLoading) return null;

  const version = contextScript?.version || contextScript?.script_version || null;
  const title = contextScript ? (contextScript.title || 'Call script') : 'No script';
  const sourceLabel = contextScriptSource
    ? String(contextScriptSource).replace(/[_-]/g, ' ')
    : null;

  return (
    <div className="csc-card shrink-0">
      {/* Header — icon + title + version + right-side tabs */}
      <div className="csc-head">
        <span className="csc-mark" aria-hidden>
          <FiMessageSquare size={13} />
        </span>
        <div className="csc-title-wrap">
          <div className="csc-title">
            {contextScriptLoading ? 'Loading script…' : title}
          </div>
          {(version || sourceLabel) && (
            <div className="csc-sub">
              {version ? `v${version}` : ''}
              {version && sourceLabel ? ' · ' : ''}
              {sourceLabel ? `via ${sourceLabel}` : ''}
            </div>
          )}
        </div>
        {showObjectionsTab && (
          <div className="csc-tabs" role="tablist">
            {tabButton(tab === 'script', 'script', 'Script', setTab)}
            {tabButton(tab === 'obj', 'obj', 'Objections', setTab)}
          </div>
        )}
      </div>

      {tab === 'script' ? (
        <>
          {/* Progress bar — one segment per step. Hidden entirely when
            * there are no steps yet (no script resolved, or still
            * loading) — it used to render unconditionally and print a
            * bare "1/0" counter in that state. */}
          {steps.length > 0 && (
            <div className="csc-progress">
              {steps.map((_, n) => (
                <button
                  key={n}
                  type="button"
                  title={steps[n]?.title || `Step ${n + 1}`}
                  onClick={() => setStepIdx(n)}
                  className={`csc-seg${
                    n < idx ? ' csc-seg--done' : n === idx ? ' csc-seg--active' : ''
                  }`}
                />
              ))}
              <span className="csc-count">{idx + 1}/{steps.length}</span>
            </div>
          )}

          {/* Body — kind badge + title + rich text */}
          {currentStep ? (
            <div className="csc-body-wrap">
              <div className="csc-step-badge">
                {currentStep.kind ? (
                  <span className="csc-pill">{currentStep.kind}</span>
                ) : null}
                <span className="csc-step-title">{currentStep.title}</span>
              </div>

              <ScriptBody html={currentStep.body} />

              {currentStep.hint && (
                <div className="csc-hint">
                  <span className="csc-hint-glyph" aria-hidden>💡</span>
                  <span>{currentStep.hint}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="csc-empty">
              {contextScriptLoading ? 'Loading script…' : 'No script assigned for this call.'}
            </div>
          )}

          {/* Foot — Back / Next-step + assist chip. Back/Next only make
            * sense once a script actually has more than one step — today
            * the backend only ever returns a flat description, which the
            * steps useMemo above wraps into a single-item array, so both
            * buttons were permanently disabled for every real script.
            * Hidden instead of shown-disabled; they'll reappear on their
            * own once a script with real multi-step data exists. */}
          {(steps.length > 1 || contextScript?.assist_hint) && (
            <div className="csc-foot">
              {steps.length > 1 && (
                <>
                  <button
                    type="button"
                    className="csc-foot-btn"
                    disabled={idx === 0}
                    onClick={() => setStepIdx((n) => Math.max(0, n - 1))}
                  >
                    <FiChevronLeft size={12} /> Back
                  </button>
                  <button
                    type="button"
                    className="csc-foot-btn csc-foot-btn--primary"
                    disabled={idx >= steps.length - 1}
                    onClick={() => setStepIdx((n) => Math.min(steps.length - 1, n + 1))}
                  >
                    Next step <FiChevronRight size={12} />
                  </button>
                </>
              )}
              {contextScript?.assist_hint && (
                <span className="csc-foot-hint">
                  <FiZap size={11} /> Assist: {contextScript.assist_hint}
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="csc-obj-list">
          {objections.length === 0 ? (
            <div className="csc-empty">No objections defined for this script.</div>
          ) : (
            objections.map((o, i) => (
              <div key={o.q || o.question || i} className="csc-obj-row">
                <div className="csc-obj-q">
                  <span className="csc-obj-dot" aria-hidden />
                  {`“${o.q || o.question || ''}”`}
                </div>
                <p className="csc-obj-a">{o.a || o.answer || ''}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
